import { useMemo, useState } from 'react';
import type { CountryCode } from 'libphonenumber-js';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { getPhoneCountries, type PhoneCountryOption } from '@/lib/phoneCountries';
import type { Language } from '@/contexts/LanguageContext';

interface PhoneInputProps {
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  language?: Language;
  className?: string;
}

export function PhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  placeholder,
  hasError = false,
  language = 'it',
  className,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const locale = language === 'it' ? 'it' : 'en';
  const countries = useMemo(() => getPhoneCountries(locale), [locale]);
  const selected = countries.find((item) => item.code === country);

  const handleSelect = (option: PhoneCountryOption) => {
    onCountryChange(option.code);
    setOpen(false);
  };

  return (
    <div className={cn('flex gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={language === 'it' ? 'Prefisso telefonico' : 'Phone country code'}
            className="w-[7.5rem] shrink-0 justify-between px-2.5 font-normal"
          >
            <span className="flex min-w-0 items-center gap-1.5 truncate">
              {selected ? (
                <>
                  <span className="text-base leading-none" aria-hidden="true">
                    {selected.flag}
                  </span>
                  <span className="truncate text-sm">{selected.dialCode}</span>
                </>
              ) : (
                <span className="text-muted-foreground text-sm">+…</span>
              )}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[18rem] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={language === 'it' ? 'Cerca paese o prefisso…' : 'Search country or code…'}
            />
            <CommandList>
              <CommandEmpty>
                {language === 'it' ? 'Nessun paese trovato.' : 'No country found.'}
              </CommandEmpty>
              <CommandGroup>
                {countries.map((option) => (
                  <CommandItem
                    key={option.code}
                    value={`${option.name} ${option.dialCode} ${option.code}`}
                    onSelect={() => handleSelect(option)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        country === option.code ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="mr-2 text-base leading-none" aria-hidden="true">
                      {option.flag}
                    </span>
                    <span className="flex-1 truncate">{option.name}</span>
                    <span className="ml-2 text-muted-foreground text-xs">{option.dialCode}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn('flex-1', hasError && 'border-destructive')}
      />
    </div>
  );
}
